import React, { useState, useEffect } from 'react';
import ProjectCard from './ProjectCard';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@300;400;500;600&display=swap');

.pj-page { font-family:'DM Sans',sans-serif; background:#f5f4f0; min-height:100vh; }

/* ── Hero ── */
.pj-hero {
  position:relative; overflow:hidden;
  padding:4rem 2rem 3.5rem;
  background:linear-gradient(135deg,#0b3d4a 0%,#134d5e 55%,#1a6474 100%);
}
.pj-orb { position:absolute;border-radius:50%;filter:blur(80px);opacity:.15;pointer-events:none; }
.pj-orb--a { width:500px;height:500px;background:radial-gradient(circle,#e67e22,transparent);top:-130px;right:-80px; }
.pj-orb--b { width:350px;height:350px;background:radial-gradient(circle,#2196a4,transparent);bottom:-80px;left:-60px; }
.pj-hero-inner { position:relative;z-index:1;max-width:800px;margin:0 auto;text-align:center; }
.pj-badge {
  display:inline-flex;align-items:center;gap:8px;
  background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.2);
  color:rgba(255,255,255,.88);font-size:.75rem;letter-spacing:.08em;
  padding:5px 14px;border-radius:999px;margin-bottom:1.25rem;
}
.pj-badge-dot { width:7px;height:7px;background:#f39c12;border-radius:50%;animation:pjPulse 2s ease-in-out infinite; }
@keyframes pjPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.5)} }
.pj-h1 {
  font-family:'Playfair Display',Georgia,serif;
  font-size:clamp(2rem,5vw,3.2rem);font-weight:700;color:#fff;
  line-height:1.15;margin:0 0 1rem;
}
.pj-gold { background:linear-gradient(90deg,#f39c12,#e67e22);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text; }
.pj-hero-sub { font-size:1rem;color:rgba(255,255,255,.68);line-height:1.7;max-width:54ch;margin:0 auto; }

/* ── Body ── */
.pj-body { max-width:1200px;margin:0 auto;padding:3rem 2rem 5rem; }

/* ── Filter bar ── */
.pj-filters {
  display:flex;align-items:center;gap:.65rem;
  flex-wrap:wrap;margin-bottom:3rem;
}
.pj-filter-label { font-size:.72rem;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:#aaa;margin-right:.25rem; }
.pj-filter-btn {
  padding:.5rem 1.2rem;border-radius:999px;border:1.5px solid rgba(0,0,0,.12);
  background:#fff;color:#555;font-family:'DM Sans',sans-serif;
  font-size:.82rem;font-weight:500;cursor:pointer;transition:all .2s;
}
.pj-filter-btn:hover { border-color:#21808d;color:#21808d; }
.pj-filter-btn.active {
  background:linear-gradient(135deg,#0b3d4a,#21808d);
  color:#fff;border-color:transparent;
  box-shadow:0 4px 14px rgba(33,128,141,.28);
}

/* ── Year section ── */
.pj-year-section { margin-bottom:3.5rem; }
.pj-year-header {
  display:flex;align-items:center;gap:1rem;
  margin-bottom:1.5rem;
}
.pj-year-badge {
  font-family:'Playfair Display',serif;font-size:1.5rem;font-weight:700;
  color:#0b3d4a;line-height:1;
}
.pj-year-tag {
  font-size:.7rem;font-weight:600;letter-spacing:.1em;text-transform:uppercase;
  padding:3px 10px;border-radius:999px;
  background:rgba(33,128,141,.1);color:#21808d;
  border:1px solid rgba(33,128,141,.2);
}
.pj-year-count {
  font-size:.78rem;color:#aaa;font-family:'DM Sans',sans-serif;
  background:#fff;border:1px solid rgba(0,0,0,.08);
  padding:3px 10px;border-radius:999px;
  margin-left:auto;
}
.pj-year-line { flex:1;height:1px;background:rgba(0,0,0,.08); }

/* ── Grid ── */
.pj-grid { display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:1.25rem; }

/* ── Loading / Error / Empty ── */
.pj-center {
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  min-height:40vh;gap:1rem;
}
.pj-spinner {
  width:36px;height:36px;
  border:3px solid rgba(33,128,141,.2);border-top-color:#21808d;
  border-radius:50%;animation:pjSpin .8s linear infinite;
}
@keyframes pjSpin { to{transform:rotate(360deg)} }
.pj-status-txt { font-size:.85rem;letter-spacing:.1em;text-transform:uppercase;color:#aaa; }
.pj-empty { text-align:center;padding:4rem 2rem;color:#aaa; }
.pj-empty-icon { font-size:2.5rem;display:block;margin-bottom:.75rem; }

@media(max-width:600px) {
  .pj-hero { padding:3rem 1.25rem 2.5rem; }
  .pj-body { padding:2rem 1.25rem 4rem; }
  .pj-grid { grid-template-columns:1fr; }
}
`;

export default function Projects() {
  const [projects, setProjects] = useState({});
  const [filter,   setFilter]   = useState('all');
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7069';

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/Projects`);
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status}: ${body}`);
        }
        const data = await res.json();
        const grouped = {};
        data.forEach(p => {
          if (!grouped[p.year]) grouped[p.year] = [];
          grouped[p.year].push(p);
        });
        setProjects(grouped);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [API_BASE_URL]);

  const getFiltered = (list) =>
    filter === 'all' ? list : list.filter(p => p.status === filter);

  const yearLabel = (yr) => {
    const y = parseInt(yr);
    const cur = new Date().getFullYear();
    if (y === cur)     return 'Current Year';
    if (y === cur - 1) return 'Last Year';
    return 'Archive';
  };

  if (loading) return (
    <div className="pj-page">
      <style>{CSS}</style>
      <div className="pj-center">
        <div className="pj-spinner"/>
        <p className="pj-status-txt">Loading projects…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="pj-page">
      <style>{CSS}</style>
      <div className="pj-center">
        <span style={{fontSize:'2.5rem'}}>🙏</span>
        <p className="pj-status-txt" style={{color:'#e57373'}}>Failed to load projects</p>
        <p style={{fontSize:'.82rem',color:'#bbb',maxWidth:'40ch',textAlign:'center'}}>{error}</p>
      </div>
    </div>
  );

  const sortedYears = Object.keys(projects).sort((a, b) => b - a);
  const hasResults = sortedYears.some(y => getFiltered(projects[y]).length > 0);

  return (
    <div className="pj-page">
      <style>{CSS}</style>

      {/* HERO */}
      <div className="pj-hero">
        <div className="pj-orb pj-orb--a"/><div className="pj-orb pj-orb--b"/>
        <div className="pj-hero-inner">
          <div className="pj-badge"><span className="pj-badge-dot"/>MSME Registered · Est. 2020</div>
          <h1 className="pj-h1">Our <span className="pj-gold">Projects</span></h1>
          <p className="pj-hero-sub">
            Explore our grassroots initiatives across education, healthcare, and community development
            — every project a step towards Swami Vivekananda's vision.
          </p>
        </div>
      </div>

      {/* BODY */}
      <div className="pj-body">

        {/* FILTERS */}
        <div className="pj-filters">
          <span className="pj-filter-label">Filter by</span>
          {[
            { val:'all',       label:'All Projects' },
            { val:'completed', label:'Completed'    },
            { val:'ongoing',   label:'Ongoing'      },
          ].map(f => (
            <button
              key={f.val}
              className={`pj-filter-btn${filter === f.val ? ' active' : ''}`}
              onClick={() => setFilter(f.val)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* YEAR GROUPS */}
        {!hasResults ? (
          <div className="pj-empty">
            <span className="pj-empty-icon">📂</span>
            <p>No {filter !== 'all' ? filter : ''} projects found.</p>
          </div>
        ) : (
          sortedYears.map(year => {
            const filtered = getFiltered(projects[year]);
            if (!filtered.length) return null;
            return (
              <div key={year} className="pj-year-section">
                <div className="pj-year-header">
                  <span className="pj-year-badge">{year}</span>
                  <span className="pj-year-tag">{yearLabel(year)}</span>
                  <div className="pj-year-line"/>
                  <span className="pj-year-count">{filtered.length} project{filtered.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="pj-grid">
                  {filtered.map(p => <ProjectCard key={p.id} project={p}/>)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}